import React from 'react';
import {useTheme} from '@mui/material/styles';

const ICONS = {
  shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
  plane: ['M17.8 19.2 16 11l3.5-3.5a2.12 2.12 0 0 0-3-3L13 8 4.8 6.2a1 1 0 0 0-.9.3l-.9.9a1 1 0 0 0 .3 1.6l4.9 2.3-2.6 2.6H3.5a1 1 0 0 0-.7.3l-.7.7a1 1 0 0 0 .3 1.6l3 1.2 1.2 3a1 1 0 0 0 1.6.3l.7-.7a1 1 0 0 0 .3-.7v-2.1l2.6-2.6 2.3 4.9a1 1 0 0 0 1.6.3l.9-.9a1 1 0 0 0 .3-.9z'],
  users: ['M16 21v-2a4 4 0 0 0-8 0v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8'],
  heart: ['M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z'],
  suitcase: ['M6 8h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2z', 'M9 8V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v3', 'M9 21V8', 'M15 21V8'],
  mountain: [
    'M4.74 15.32m-3.5 0a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0',
    'M17.1 19.34m-3.5 0a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0',
    'M17.63 6.37m-1.2 0a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0 -2.4 0',
    'M10.92 17.33L12 14L10.07 10.22L14.81 8.6L15.78 12.07L17.68 12.69',
    'M0.3 10.5l3-1',
    'M1 7.8l3-1',
  ],
  snow: ['M12 2v20', 'M4.2 7l15.6 10', 'M19.8 7L4.2 17', 'M12 2l-2.5 2.5M12 2l2.5 2.5', 'M12 22l-2.5-2.5M12 22l2.5-2.5', 'M4.2 7l.4 3.4M4.2 7l3.3-.9', 'M19.8 17l-.4-3.4M19.8 17l-3.3.9', 'M19.8 7l-3.3-.9M19.8 7l-.4 3.4', 'M4.2 17l3.3.9M4.2 17l.4-3.4'],
  calendarX: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'M9.5 14l5 4', 'M14.5 14l-5 4'],
  laptop: ['M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z', 'M2 20h20'],
  mobile: ['M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z', 'M11 18h2'],
  phone: ['M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z'],
  car: ['M5 17h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2z', 'M7 10l2-5h6l2 5', 'M7.5 14.5h.01', 'M16.5 14.5h.01'],
  check: ['M20 6 9 17l-5-5'],
  chevron: ['M6 9l6 6 6-6'],
};

export function IconBox({icon, size = 52, circular = false, selected = false, elevated = false}) {
  const theme = useTheme();
  const paths = ICONS[icon] || ICONS.shield;
  const compactBackground = selected ? theme.palette.primary.lighter : theme.palette.action.selected;
  const compactColor = selected ? theme.palette.primary.dark : theme.palette.secondary.main;
  return (
    <span data-coverage-icon={circular ? 'compact' : undefined} style={{
      width: size,
      height: size,
      flex: 'none',
      borderRadius: circular ? '50%' : elevated ? theme.shape.borderRadius : 12,
      background: circular ? compactBackground : theme.palette.background.paper,
      border: circular || elevated ? 'none' : `1.5px solid ${theme.palette.secondary.main}`,
      boxShadow: elevated ? theme.shadows[3] : 'none',
      color: circular ? compactColor : theme.palette.secondary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 150ms ease-out, color 150ms ease-out',
    }}>
      <svg width={Math.round(size * 0.46)} height={Math.round(size * 0.46)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {paths.map((d, i) => <path key={i} d={d} />)}
      </svg>
    </span>
  );
}

export function Ic({name, size = 18}) {
  const paths = ICONS[name] || [];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}
